import styled, { css, keyframes } from 'styled-components'
import { AbsoluteColor, buttonReset, linkReset, spacing, themeColor, Typography } from '~theme'
import { IconStyled } from '~components/Icon'
import { IconPositionType, ShapeType, SizeType, StyledButtonProps } from './Button.types'
import * as utils from './Button.utils'

export const ButtonCover = styled.button`
  ${linkReset};
  ${buttonReset};
  vertical-align: middle;
`

export const Container = styled.button<StyledButtonProps>`
  ${linkReset};
  ${buttonReset};
  cursor: ${props => utils.getCursor(props)};
  overflow: visible;
  position: relative;
  display: inline-block;
  vertical-align: middle;
  width: ${props => props.isFullWidth && '100%'};
  border-radius: ${props => utils.getBorderRadius(props.shape)};
  border: 1px solid transparent;
  outline: 1px solid transparent;
  outline-offset: -1px;
  user-select: none;

  ${({ size, isHeightAuto }) => css`
    height: ${isHeightAuto ? 'auto' : utils.getSize(size)};
    min-height: ${isHeightAuto && utils.getSize(size)};
    padding: 0 ${utils.getPadding(size)};
    ${utils.getTypography(size)};
    ${Typography.textSemiStrong};
  `};

  ${props => utils.getShape(props.shape, props.size)};
  ${props => utils.getView(props.view)(props).static};

  ${props =>
    props.isDisabled &&
    !props.isSuccess &&
    !props.isLoading &&
    css`
      ${utils.getView(props.view)(props)?.disabled || 'opacity: 0.5;'};
    `}

  ${props =>
    !props.isDisabled &&
    !props.isSuccess &&
    !props.isLoading &&
    css`
      &:hover,
      ${ButtonCover}:hover & {
        ${utils.getView(props.view)(props).hover};
      }

      &:active,
      ${ButtonCover}:active & {
        ${utils.getView(props.view)(props).active};
      }

      &:focus,
      ${ButtonCover}:focus & {
        outline: 1px solid transparent;
      }

      &:focus-visible,
      ${ButtonCover}:focus-visible & {
        z-index: 2;
        box-shadow: none;
        ${utils.getView(props.view)(props).focus || `outline-color: ${themeColor(AbsoluteColor.max)}`};
      }
    `};

  ${props =>
    (props.isActive || props.isLoading) &&
    css`
      ${utils.getView(props.view)(props).active};
    `};
`

interface InnerProps {
  isHidden: boolean
  shape: ShapeType
}

export const Inner = styled.div<InnerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  transition: opacity 0.15s, visibility 0.15s;

  ${props =>
    props.isHidden &&
    css`
      opacity: 0;
      visibility: hidden;
    `};

  ${IconStyled} {
    ${props => utils.getShapeIconSize(props.shape)};
  }
`

interface IconSideProps {
  size: SizeType
  position?: IconPositionType
}

export const IconSide = styled.div<IconSideProps>`
  line-height: 0;

  ${props =>
    props.position === 'left' &&
    css`
      margin-left: ${utils.getIconOffset(props.size)};
      margin-right: ${spacing(0.25)};
    `};

  ${props =>
    props.position === 'right' &&
    css`
      margin-left: ${spacing(0.25)};
      margin-right: ${utils.getIconOffset(props.size)};
    `};
`

const showMiddleElement = keyframes`
  from {
    opacity: 0;
    transform: scale(2.5);
  }
`

export const middleElementAnimation = css`
  animation: ${showMiddleElement} 0.3s forwards;
`

export const middleElement = css`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  margin: auto;
`

interface IconMiddleProps {
  isAnimated?: boolean
}

export const IconMiddle = styled.div<IconMiddleProps>`
  ${middleElement};
  ${props => props.isAnimated && middleElementAnimation};
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

interface SuccessTextProps {
  size: SizeType
}

export const SuccessText = styled.span<SuccessTextProps>`
  ${middleElement};
  ${middleElementAnimation};
  display: flex;
  justify-content: center;
  align-items: center;
`
